import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  LocationOn as LocationIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { collection, addDoc, serverTimestamp, db } from '../firebase/firebase';

// 导入图片
import aboutBg from '../assets/images/about/aboutBg.png';

const Contact = () => {
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;
    
    if (!formValues.name.trim()) {
      errors.name = '请输入您的姓名';
      isValid = false;
    }
    
    if (!formValues.email.trim()) {
      errors.email = '请输入您的邮箱';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = '邮箱格式不正确';
      isValid = false;
    }
    
    if (!formValues.subject.trim()) {
      errors.subject = '请输入主题';
      isValid = false;
    }
    
    if (!formValues.message.trim()) {
      errors.message = '请输入留言内容';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        // 设置加载状态
        setLoading(true);
        
        // 显示发送中状态
        setSnackbar({
          open: true,
          message: '正在发送您的留言...',
          severity: 'info'
        });
        
        // 创建要提交到 mail 集合的数据对象
        const mailData = {
          name: formValues.name,
          email: formValues.email,
          subject: `网站新联系表单: ${formValues.subject}`,
          messageContent: formValues.message,
          createdAt: serverTimestamp(),
          status: 'new',
          read: false,
          source: 'website_contact_form',
          to: ['dylan@martha-trade.com'] // 管理员邮箱（接收通知用）
        };
        
        // 写入到 Firestore 的 mail 集合
        const docRef = await addDoc(collection(db, 'mail'), mailData);
        
        console.log('联系表单已提交到 Firestore，文档ID:', docRef.id);
        
        // 显示成功消息
        setSnackbar({
          open: true,
          message: '您的留言已成功发送！我们会尽快回复您。',
          severity: 'success'
        });
        
        // 重置表单
        setFormValues({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } catch (error) {
        console.error('发送留言失败:', error);
        setSnackbar({
          open: true,
          message: '发送留言失败，请稍后重试。',
          severity: 'error'
        });
      } finally {
        // 无论成功或失败，都清除加载状态
        setLoading(false);
      }
    } else {
      setSnackbar({
        open: true,
        message: '请检查表单内容并重试。',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', mb: 6 }}>
        <img 
          src={aboutBg} 
          alt="Contact Us Background" 
          style={{ width: '100%', display: 'block' }}
        />
        <Box 
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: '100%',
            padding: '0 2rem',
            color: 'white'
          }}
        >
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            联系我们
          </Typography>
          <Typography variant="h5">
            Contact Us
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Typography variant="h4" gutterBottom>
              联系方式
            </Typography>
            <Typography variant="h5" gutterBottom>
              Contact Info
            </Typography>
            <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 3, borderRadius: '2px' }} />
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" paragraph>
                如有任何问题或建议，欢迎通过以下方式联系我们。我们的客服团队会尽快回复您的咨询。
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmailIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  电子邮箱
                </Typography>
                <Typography variant="body1">
                  dylan@martha-trade.com
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PhoneIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  联系电话
                </Typography>
                <Typography variant="body1">
                  +65 91761341
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LocationIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  公司地址
                </Typography>
                <Typography variant="body1">
                  10 MARINA BOULEVARD MARINA BAY FINANCIAL CENTRE SINGAPORE (018983)
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 6 }}>
              <Typography variant="h6" gutterBottom>
                营业时间
              </Typography>
              <Typography variant="body1" paragraph>
                周一至周五: 9:00 - 18:00
              </Typography>
              <Typography variant="body1">
                周六至周日: 10:00 - 16:00
              </Typography>
            </Box>
          </Grid>
          
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
              <Typography variant="h4" gutterBottom>
                发送留言
              </Typography>
              <Typography variant="h5" gutterBottom>
                Send Message
              </Typography>
              <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 3, borderRadius: '2px' }} />
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="您的姓名 (Your Name)"
                      name="name"
                      value={formValues.name}
                      onChange={handleChange}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="电子邮箱 (Email)"
                      name="email"
                      type="email"
                      value={formValues.email}
                      onChange={handleChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="主题 (Subject)"
                      name="subject"
                      value={formValues.subject}
                      onChange={handleChange}
                      error={!!formErrors.subject}
                      helperText={formErrors.subject}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="留言内容 (Message)"
                      name="message"
                      multiline
                      rows={6}
                      value={formValues.message}
                      onChange={handleChange}
                      error={!!formErrors.message}
                      helperText={formErrors.message}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      disabled={loading}
                      sx={{ 
                        mt: 2,
                        px: 4,
                        py: 1.5
                      }}
                    >
                      {loading ? '发送中...' : '发送留言'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Map Section */}
      <Box sx={{ height: '400px', width: '100%', mb: 8 }}>
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.826162362721!2d103.85178741470276!3d1.280301999069937!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19119395b0e3%3A0x42064fa125f35f8!2sMarina%20Bay%20Financial%20Centre!5e0!3m2!1sen!2ssg!4v1679648073316!5m2!1sen!2ssg" 
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen="" 
          loading="lazy"
          title="Destiny-Crystal Location"
        ></iframe>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;